import { watchEffect } from 'vue'

import { useSiteConfig } from './useSiteConfig'

/** Mezcla dos colores hex en proporción t (0..1). */
function mix(a: [number, number, number], b: [number, number, number], t: number): string {
  const r = Math.round(a[0] + (b[0] - a[0]) * t)
  const g = Math.round(a[1] + (b[1] - a[1]) * t)
  const bb = Math.round(a[2] + (b[2] - a[2]) * t)
  return `#${[r, g, bb].map((n) => n.toString(16).padStart(2, '0')).join('')}`
}

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

const WHITE: [number, number, number] = [255, 255, 255]
const BLACK: [number, number, number] = [15, 23, 42] // slate-900, para sombras menos crudas

/** Ratios de mezcla por tono (500 = base). */
const RAMP: Record<number, { to: 'w' | 'b'; t: number }> = {
  50: { to: 'w', t: 0.92 },
  100: { to: 'w', t: 0.82 },
  200: { to: 'w', t: 0.64 },
  300: { to: 'w', t: 0.46 },
  400: { to: 'w', t: 0.24 },
  500: { to: 'w', t: 0 },
  600: { to: 'b', t: 0.16 },
  700: { to: 'b', t: 0.32 },
  800: { to: 'b', t: 0.46 },
  900: { to: 'b', t: 0.58 },
  950: { to: 'b', t: 0.74 },
}

/**
 * Aplica el color de acento configurado en el admin como variables CSS
 * `--p-primary-*`. Tailwind (`primary-*`) y PrimeVue leen esas variables, por lo
 * que recolorea toda la app en vivo. Llamar una vez en App.vue.
 */
export function useTheme(): void {
  const { config } = useSiteConfig()

  watchEffect(() => {
    if (typeof document === 'undefined') return
    const hex = (config.value.tema_color_primario as string) || '#C8AA5A'
    const base = hexToRgb(hex)
    if (!base) return

    const root = document.documentElement
    for (const [shade, spec] of Object.entries(RAMP)) {
      const color = mix(base, spec.to === 'w' ? WHITE : BLACK, spec.t)
      root.style.setProperty(`--p-primary-${shade}`, color)
    }
    // Tokens que usan los componentes PrimeVue.
    root.style.setProperty('--p-primary-color', hex)
    root.style.setProperty('--p-primary-contrast-color', '#ffffff')
    root.style.setProperty('--p-primary-hover-color', mix(base, BLACK, 0.16))
    root.style.setProperty('--p-primary-active-color', mix(base, BLACK, 0.32))
    root.style.setProperty('--color-gold', hex)
  })
}
